import type {Ref} from 'vue';
import {
	clearGuestStorage,
	createGuestListGtmId,
	getInitialGuestState,
	useGuestShoppingListStorage,
} from './storage';
import type {
	CreateItemPayload,
	GuestShoppingList,
	GuestShoppingListItem,
	GuestShoppingListsState,
	ShoppingListRepository,
} from './types';
import {
	DEFAULT_GUEST_LIST_NAME,
	GuestShoppingListStorageError,
	MAX_GUEST_ITEMS_PER_LIST,
	MAX_GUEST_LISTS,
} from './types';
import type {
	Gsoa_ShoppingList_GetAllShoppingLists_response as AllShoppingLists,
	Gsoa_ShoppingList_GetAllShoppingListsCollection as ShoppingList,
	Gsoa_ShoppingList_GetItemsCollection as GetItemsCollection,
} from '~/openapi';
import {getActiveShoppingListItemsCount} from '~/utils/shoppingList';

function generateGuestCode(): string {
	return Math.random().toString(36).slice(2, 11).padEnd(9, '0').slice(0, 9);
}

function toApiList(list: GuestShoppingList): ShoppingList {
	return {
		ID: list.ID,
		Name: list.Name,
		ItemsCount: getActiveShoppingListItemsCount(list.items),
		IsSelected: list.IsSelected,
		IsActive: false,
		ApplicationType: 'web-guest',
	};
}

function toApiItem(item: GuestShoppingListItem): GetItemsCollection & {CreatedAt?: string} {
	return {
		ID: item.ID,
		Code: item.Code,
		Text: item.Text,
		VANR: item.VANR,
		Amount: item.Amount,
		Purchased: item.Purchased,
		GeneralizedProduct: item.GeneralizedProduct,
		LastUpdate: item.LastUpdate,
		LastUpdateBy: item.LastUpdateBy,
		VisualOrder: item.VisualOrder,
		CreatedAt: item.CreatedAt ?? item.LastUpdate,
	};
}

function touchState(storage: Ref<GuestShoppingListsState>) {
	storage.value = {
		...storage.value,
		lastModified: new Date().toISOString(),
	};
}

function nextListId(storage: Ref<GuestShoppingListsState>): number {
	const id = storage.value.idCounter;
	storage.value.idCounter = id - 1;
	return id;
}

function nextItemId(storage: Ref<GuestShoppingListsState>): number {
	const id = storage.value.itemIdCounter;
	storage.value.itemIdCounter = id - 1;
	return id;
}

function ensureDefaultList(storage: Ref<GuestShoppingListsState>): GuestShoppingList {
	if (storage.value.lists.length === 0) {
		const id = nextListId(storage);
		const list: GuestShoppingList = {
			ID: id,
			Name: DEFAULT_GUEST_LIST_NAME,
			ItemsCount: 0,
			IsSelected: true,
			IsActive: false,
			ApplicationType: 'web-guest',
			gtmUuid: createGuestListGtmId(),
			items: [],
		};
		storage.value.lists.push(list);
		touchState(storage);
	}
	return storage.value.lists.find((l) => l.IsSelected) ?? storage.value.lists[0];
}

function findList(storage: Ref<GuestShoppingListsState>, listId: string): GuestShoppingList | undefined {
	return storage.value.lists.find((list) => list.ID.toString() === listId);
}

function syncListCounts(list: GuestShoppingList) {
	list.ItemsCount = getActiveShoppingListItemsCount(list.items);
}

export function createLocalShoppingListRepository(): ShoppingListRepository & {storage: Ref<GuestShoppingListsState>; clearAll: () => void} {
	const storage = import.meta.client
		? useGuestShoppingListStorage()
		: ref(getInitialGuestState());

	function toAllShoppingLists(): AllShoppingLists {
		return {
			Items: storage.value.lists.map(toApiList),
			totalCount: storage.value.lists.length,
		};
	}

	return {
		storage,

		clearAll() {
			clearGuestStorage(storage);
		},

		invalidateCache() {},

		fetchLists() {
			if (!import.meta.client) {
				return Promise.resolve({Items: [], totalCount: 0});
			}
			ensureDefaultList(storage);
			return Promise.resolve(toAllShoppingLists());
		},

		fetchItems(listId?: string) {
			if (!import.meta.client) {
				return Promise.resolve([]);
			}

			const listsToFetch = listId
				? storage.value.lists.filter((l) => l.ID.toString() === listId)
				: storage.value.lists;

			return Promise.resolve(
				listsToFetch.map((list) => ({
					id: list.ID,
					products: {Items: list.items.map(toApiItem)},
				})),
			);
		},

		createList(name: string) {
			if (!import.meta.client) {
				return Promise.resolve(null);
			}
			if (storage.value.lists.length >= MAX_GUEST_LISTS) {
				return Promise.reject(new GuestShoppingListStorageError('MAX_LISTS_REACHED'));
			}

			const id = nextListId(storage);
			const createdAt = new Date().toISOString();
			const list: GuestShoppingList = {
				ID: id,
				Name: name,
				ItemsCount: 0,
				IsSelected: storage.value.lists.length === 0,
				IsActive: false,
				ApplicationType: 'web-guest',
				CreatedAt: createdAt,
				gtmUuid: createGuestListGtmId(),
				items: [],
			};
			storage.value.lists.push(list);
			touchState(storage);
			return Promise.resolve(toApiList(list));
		},

		updateList(listId: string, name: string, isSelected?: boolean) {
			const list = findList(storage, listId);
			if (!list) {
				return Promise.resolve(null);
			}
			list.Name = name;
			if (isSelected) {
				storage.value.lists.forEach((l) => {
					l.IsSelected = l.ID === list.ID;
				});
			}
			touchState(storage);
			return Promise.resolve(toApiList(list));
		},

		deleteList(listId: string) {
			const index = storage.value.lists.findIndex((l) => l.ID.toString() === listId);
			if (index === -1) {
				return Promise.resolve(false);
			}
			const wasSelected = storage.value.lists[index].IsSelected;
			storage.value.lists.splice(index, 1);
			if (wasSelected && storage.value.lists.length > 0) {
				storage.value.lists[0].IsSelected = true;
			}
			touchState(storage);
			return Promise.resolve(true);
		},

		createItem(shoppingListId: string, item: CreateItemPayload) {
			if (!import.meta.client) {
				return Promise.resolve(null);
			}

			let list = findList(storage, shoppingListId);
			if (!list) {
				list = ensureDefaultList(storage);
			}

			if (list.items.length >= MAX_GUEST_ITEMS_PER_LIST) {
				return Promise.reject(new GuestShoppingListStorageError('MAX_ITEMS_REACHED'));
			}

			const hasVanr = !!item.VANR;
			const normalizedText = item.Text.trim().toLowerCase();
			const existing = list.items.find((i) => {
				if (hasVanr) {
					return i.VANR === item.VANR;
				}
				return !i.VANR && i.Text.trim().toLowerCase() === normalizedText;
			});

			if (existing) {
				existing.Amount = hasVanr
					? Math.min(99, (existing.Amount ?? 1) + (item.Amount ?? 1))
					: 1;
				syncListCounts(list);
				touchState(storage);
				return Promise.resolve(toApiItem(existing));
			}

			const now = new Date().toISOString();
			const newItem: GuestShoppingListItem = {
				ID: nextItemId(storage),
				Code: generateGuestCode(),
				Text: item.Text ?? '',
				VANR: item.VANR ?? null,
				Amount: hasVanr ? (item.Amount ?? 1) : 1,
				Purchased: false,
				GeneralizedProduct: false,
				LastUpdate: now,
				LastUpdateBy: 'guest',
				VisualOrder: list.items.length,
				CreatedAt: now,
			};

			list.items.push(newItem);
			syncListCounts(list);
			touchState(storage);
			return Promise.resolve(toApiItem(newItem));
		},

		updateItem(shoppingListId: string, itemId: string, item: CreateItemPayload) {
			const list = findList(storage, shoppingListId);
			if (!list) {
				return Promise.resolve(null);
			}
			const existing = list.items.find((i) => i.ID.toString() === itemId);
			if (!existing) {
				return Promise.resolve(null);
			}
			if (item.Text !== undefined) {
				existing.Text = item.Text;
			}
			if (item.Amount !== undefined && item.Amount !== null) {
				existing.Amount = Math.min(99, Math.max(1, item.Amount));
			}
			if (item.Purchased !== undefined) {
				existing.Purchased = item.Purchased;
			}
			existing.LastUpdate = new Date().toISOString();
			syncListCounts(list);
			touchState(storage);
			return Promise.resolve(toApiItem(existing));
		},

		deleteItem(shoppingListId: string, itemId: string) {
			const list = findList(storage, shoppingListId);
			if (!list) {
				return Promise.resolve(false);
			}
			const index = list.items.findIndex((i) => i.ID.toString() === itemId);
			if (index === -1) {
				return Promise.resolve(false);
			}
			list.items.splice(index, 1);
			syncListCounts(list);
			touchState(storage);
			return Promise.resolve(true);
		},

		clearItems(shoppingListId: string) {
			const list = findList(storage, shoppingListId);
			if (!list) {
				return Promise.resolve(false);
			}
			list.items = [];
			syncListCounts(list);
			touchState(storage);
			return Promise.resolve(true);
		},

		reorderItems(shoppingListId: string, orderedItemIds: number[], _sourceItems?: unknown) {
			const list = findList(storage, shoppingListId);
			if (!list) {
				return Promise.resolve();
			}

			const itemsById = new Map(list.items.map((item) => [item.ID, item]));
			const reordered: GuestShoppingListItem[] = [];

			orderedItemIds.forEach((id) => {
				const item = itemsById.get(id);
				if (item) {
					reordered.push(item);
					item.VisualOrder = reordered.length;
					itemsById.delete(id);
				}
			});

			itemsById.forEach((item) => {
				reordered.push(item);
				item.VisualOrder = reordered.length;
			});

			list.items = reordered;
			touchState(storage);
			return Promise.resolve();
		},
	};
}
